/**
 * AppointmentCard — tarjeta premium unificada para citas del terapeuta.
 * Maneja dos variantes: 'upcoming' (confirmadas/aceptadas) y 'pending' (por aceptar).
 * Diseño: Isabel Moreno (UX/UI Lead) · Implementación: Sofía García (Frontend)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
    DARKER_TEXT,
    DARK_TEXT,
    BRAND_GREEN_PRIMARY,
    INFO_BLUE,
    WARNING_ORANGE,
    SUCCESS_GREEN,
} from 'resources/constants/colors';
import { IMAGES_URL } from 'resources/constants/urls';
import NoProfileSVG from 'resources/img/no-pic-therapist.png';
import { getDisplayDate } from 'utils/date';
import { getDisplayTime } from 'utils/time';
import Button from 'components/Button';
import useAppointments from 'state/appointments';
import { Ring } from '@uiball/loaders';
import { subscribeNotificationsIfNotAlready } from 'utils/notifications';

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div`
    background: #fff;
    border-radius: 14px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: ${slideIn} 0.3s ease both;
    border-left: 3px solid ${({ variant }) =>
        variant === 'pending' ? WARNING_ORANGE :
        variant === 'upcoming' ? SUCCESS_GREEN : BRAND_GREEN_PRIMARY};
`;

const CardRow = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    text-decoration: none;
    color: ${DARKER_TEXT};
`;

const Avatar = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const Info = styled.div`
    flex: 1;
    min-width: 0;
`;

const Name = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DateText = styled.p`
    font-size: 12px;
    color: ${DARK_TEXT};
    margin: 0 0 4px;
`;

const Badge = styled.span`
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    border-radius: 8px;
    padding: 2px 7px;
    background: ${({ bg }) => bg || `${BRAND_GREEN_PRIMARY}18`};
    color: ${({ color }) => color || BRAND_GREEN_PRIMARY};
`;

const AcceptBtn = styled(Button)`
    width: 100%;
    padding: 8px;
    font-size: 13px;
    font-weight: 700;
`;

const AppointmentCard = ({ app, variant }) => {
    const [appointments, appointmentsDispatcher] = useAppointments();
    const isAccepting = appointments.fetching.state && appointments.fetching.config.key === 'accept';
    const isFirstTime = app.isFirstAppointment || app.appointmentNumber === 1;

    const handleAccept = (e) => {
        e.preventDefault();
        appointmentsDispatcher.acceptStart({ appointmentId: app.id });
        subscribeNotificationsIfNotAlready();
    };

    const badgeConfig = {
        upcoming: { bg: `${SUCCESS_GREEN}18`, color: SUCCESS_GREEN, label: '✅ Confirmada' },
        pending:  { bg: `${WARNING_ORANGE}18`, color: WARNING_ORANGE, label: '⏳ Por aceptar' },
    };
    const badge = badgeConfig[variant] || badgeConfig.upcoming;
    const sessionTypeBadgeBg = isFirstTime ? `${INFO_BLUE}18` : `${BRAND_GREEN_PRIMARY}18`;
    const sessionTypeBadgeColor = isFirstTime ? INFO_BLUE : BRAND_GREEN_PRIMARY;
    const sessionTypeLabel = isFirstTime ? '🆕 Primera vez' : '🔄 Seguimiento';

    return (
        <Card variant={variant}>
            <CardRow as={Link} to={`/appointment/${app.roomId}`}>
                <Avatar>
                    <img
                        src={app.profileImg ? `${IMAGES_URL}${app.profileImg}` : NoProfileSVG}
                        alt={`${app.name} ${app.lastName}`}
                    />
                </Avatar>
                <Info>
                    <Name>{`${app.title ?? ''} ${app.name} ${app.lastName}`.trim()}</Name>
                    <DateText>
                        {getDisplayDate(app.date, 'EEEE d MMM')} · {getDisplayTime(app.date)}
                    </DateText>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Badge bg={badge.bg} color={badge.color}>{badge.label}</Badge>
                        <Badge bg={sessionTypeBadgeBg} color={sessionTypeBadgeColor}>{sessionTypeLabel}</Badge>
                    </div>
                </Info>
            </CardRow>

            {variant === 'pending' && (
                <AcceptBtn
                    onClick={handleAccept}
                    disabled={isAccepting}
                >
                    {isAccepting ? <Ring color="#fbfbfd" size={20} /> : 'Aceptar cita'}
                </AcceptBtn>
            )}
        </Card>
    );
};

export default AppointmentCard;
